import Modal from 'src/components/form/fou-dialog/';
import { Toaster } from 'src/utils/helper/toaster';
import { HAS_EMAIL_SENT_ONCE_KEY, RESEND_EMAIL_COOLDOWN_TIME } from 'src/utils/constants/common';
import { MdButton, MdModal, MdTypography } from '@antiqua-dls/vue-components';
import { adobeCjaEventEmitter } from 'modules/adobe-cja/utils';
import { CJA_EVENTS, LINK_CATEGORIES, LINK_NAMES, LINK_TYPES } from 'modules/adobe-cja/constants';

const PAYMENT_ROUTES = ['payment_new_data_manager', 'payment_edit_data_manager'];

export default {
  components: {
    Modal,
    MdButton,
    MdModal,
    MdTypography,
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close'],
  data() {
    return {
      remainingTime: RESEND_EMAIL_COOLDOWN_TIME,
      interval: null,
      cooldownKey: 'fouEmailVerifyCooldownEndsAt',
      cooldownSeconds: RESEND_EMAIL_COOLDOWN_TIME,
      hasEmailSentOnceState: localStorage.getItem(HAS_EMAIL_SENT_ONCE_KEY) === 'true',
    };
  },
  computed: {
    formattedTime() {
      const minutes = Math.floor(this.remainingTime / 60);
      const seconds = this.remainingTime % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    },
    minuteUnit() {
      if (this.remainingTime < 60) {
        return `${this.remainingTime <= 1 ? 'second' : 'seconds'}`;
      }
      return 'minutes';
    },
    secondaryDescription() {
      if (this.remainingTime <= 0) {
        return this.$t('components.general.verify_email.modal.content_without_timer');
      }
      return this.$t('components.general.verify_email.modal.content_with_timer', {
        timer: this.formattedTime,
        unit: this.minuteUnit,
      });
    },
    confirmButtonLabel() {
      return this.hasEmailSentOnceState
        ? this.$t('components.general.verify_email.modal.resend_button_label')
        : this.$t('components.general.verify_email.modal.send_button_label');
    },
  },
  watch: {
    isOpen: {
      immediate: true,
      handler(isOpen) {
        // Keep timer in sync whenever modal is shown
        if (!isOpen) return;
        const endsAtMs = this.getCooldownEndsAt();
        if (endsAtMs > Date.now()) this.startTimer(endsAtMs);
        else {
          this.clearCooldownEndsAt();
          this.remainingTime = 0;
        }
      },
    },
  },
  beforeUnmount() {
    this.stopTimer();
  },
  methods: {
    onClose() {
      const isPaymentContext = PAYMENT_ROUTES.includes(this.$route?.name);
      if (!isPaymentContext) {
        adobeCjaEventEmitter({
          event: CJA_EVENTS.LINK_CLICK,
          links: {
            linkCategory: LINK_CATEGORIES.EMAIL_SENT_DIALOG_BOX,
            linkSection: window?.pageInfoGlobal?.pageInfo?.pageSection || '',
            linkName: LINK_NAMES.CLOSE,
            linkType: LINK_TYPES.ICON
          }
        });
      }
      this.$emit('close');
    },
    getCooldownEndsAt() {
      const raw = window.localStorage.getItem(this.cooldownKey);
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : 0;
    },
    setCooldownEndsAt(endsAtMs) {
      window.localStorage.setItem(this.cooldownKey, String(endsAtMs));
    },
    clearCooldownEndsAt() {
      window.localStorage.removeItem(this.cooldownKey);
    },
    updateRemainingTimeFromEndsAt(endsAtMs) {
      const diffMs = endsAtMs - Date.now();
      this.remainingTime = Math.max(0, Math.ceil(diffMs / 1000));
    },
    startTimer(endsAtMs) {
      this.stopTimer();
      this.updateRemainingTimeFromEndsAt(endsAtMs);
      this.interval = window.setInterval(() => {
        this.updateRemainingTimeFromEndsAt(endsAtMs);
        if (this.remainingTime <= 0) {
          this.stopTimer();
          this.clearCooldownEndsAt();
        }
      }, 1000);
    },
    stopTimer() {
      if (this.interval) {
        window.clearInterval(this.interval);
        this.interval = null;
      }
    },
    async onConfirm() {
      const isPaymentContext = PAYMENT_ROUTES.includes(this.$route?.name);
      if (isPaymentContext) {
        adobeCjaEventEmitter({
          event: CJA_EVENTS.LINK_CLICK,
          links: {
            linkCategory: LINK_CATEGORIES.EMAIL_SENT_DIALOG_BOX_PAYMENT_SETUP,
            linkSection: window?.pageInfoGlobal?.pageInfo?.pageSection || '',
            linkName: LINK_NAMES.RESEND_EMAIL_PAYMENT_SETUP,
            linkType: LINK_TYPES.BUTTON
          }
        });
      } else {
        adobeCjaEventEmitter({
          event: CJA_EVENTS.LINK_CLICK,
          links: {
            linkCategory: LINK_CATEGORIES.EMAIL_SENT_DIALOG_BOX,
            linkSection: window?.pageInfoGlobal?.pageInfo?.pageSection || '',
            linkName: this.hasEmailSentOnceState
              ? LINK_NAMES.RESEND_EMAIL
              : LINK_NAMES.SEND_EMAIL,
            linkType: LINK_TYPES.ICON
          }
        });
      }

      // Close immediately, then start cooldown & send email.
      this.$emit('close');

      const endsAtMs = Date.now() + this.cooldownSeconds * 1000;
      this.setCooldownEndsAt(endsAtMs);
      this.startTimer(endsAtMs);

      // setting email sent is true on clicking confirm button
      localStorage.setItem(HAS_EMAIL_SENT_ONCE_KEY, 'true');

      this.hasEmailSentOnceState = true;

      try {
        const response = await this.$store.dispatch('auth/sendEmailVerification');
        if (response?.data?.success === true) {
          Toaster.notifySuccess(this.$t('components.general.verify_email.toast.email_success'));
        } else {
          this.remainingTime = 0;
          this.stopTimer();
          this.clearCooldownEndsAt();
          Toaster.notifyError(this.$t('components.general.verify_email.toast.email_failed'));
        }
      } catch {
        this.remainingTime = 0;
        this.stopTimer();
        this.clearCooldownEndsAt();
        Toaster.notifyError(this.$t('components.general.verify_email.toast.email_failed'));
      }
    },
  },
};
